'use client';

import { useState } from 'react';

interface Calculator401kState {
  currentAge: number;
  retirementAge: number;
  currentSavings: number;
  monthlyContribution: number;
  employerMatch: number;
  annualReturn: number;
}

const INITIAL_STATE: Calculator401kState = {
  currentAge: 30,
  retirementAge: 65,
  currentSavings: 50000,
  monthlyContribution: 500,
  employerMatch: 3,
  annualReturn: 7,
};

const PRESETS: { [key: string]: Calculator401kState } = {
  conservative: {
    currentAge: 30,
    retirementAge: 67,
    currentSavings: 25000,
    monthlyContribution: 300,
    employerMatch: 3,
    annualReturn: 5,
  },
  moderate: {
    currentAge: 30,
    retirementAge: 65,
    currentSavings: 50000,
    monthlyContribution: 500,
    employerMatch: 4,
    annualReturn: 7,
  },
  aggressive: {
    currentAge: 30,
    retirementAge: 62,
    currentSavings: 100000,
    monthlyContribution: 1000,
    employerMatch: 6,
    annualReturn: 9,
  },
};

export default function Calculator401k() {
  const [inputs, setInputs] = useState<Calculator401kState>(INITIAL_STATE);

  const calculate = () => {
    const years = inputs.retirementAge - inputs.currentAge;
    const monthlyRate = inputs.annualReturn / 100 / 12;
    const months = years * 12;
    const employerContribution = inputs.monthlyContribution * (inputs.employerMatch / 100);
    const totalMonthly = inputs.monthlyContribution + employerContribution;

    const futureValue =
      inputs.currentSavings * Math.pow(1 + monthlyRate, months) +
      totalMonthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);

    const totalContributions = inputs.currentSavings + totalMonthly * months;
    const earnings = futureValue - totalContributions;

    return {
      futureValue,
      totalContributions,
      earnings,
      years,
    };
  };

  const results = calculate();

  const handleReset = () => {
    setInputs(INITIAL_STATE);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setInputs(PRESETS[preset]);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      {/* Input Section - 2 columns */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-slate-900">Your Information</h2>
          </div>

          {/* Preset and Reset Buttons */}
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handlePreset('conservative')}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
              >
                Conservative
              </button>
              <button
                onClick={() => handlePreset('moderate')}
                className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
              >
                Moderate
              </button>
              <button
                onClick={() => handlePreset('aggressive')}
                className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
              >
                Aggressive
              </button>
            </div>
            <button
              onClick={handleReset}
              className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
            >
              Reset to Default
            </button>
          </div>

          <div className="space-y-6">
            <InputField
              label="Current Age"
              value={inputs.currentAge}
              onChange={(v) => setInputs({ ...inputs, currentAge: v })}
              min={18}
              max={100}
              unit="years"
            />
            <InputField
              label="Retirement Age"
              value={inputs.retirementAge}
              onChange={(v) => setInputs({ ...inputs, retirementAge: v })}
              min={inputs.currentAge + 1}
              max={100}
              unit="years"
            />
            <InputField
              label="Current 401(k) Balance"
              value={inputs.currentSavings}
              onChange={(v) => setInputs({ ...inputs, currentSavings: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Monthly Contribution"
              value={inputs.monthlyContribution}
              onChange={(v) => setInputs({ ...inputs, monthlyContribution: v })}
              min={0}
              prefix="$"
            />
            <InputField
              label="Employer Match"
              value={inputs.employerMatch}
              onChange={(v) => setInputs({ ...inputs, employerMatch: v })}
              min={0}
              max={100}
              step={0.5}
              unit="%"
            />
            <InputField
              label="Expected Annual Return"
              value={inputs.annualReturn}
              onChange={(v) => setInputs({ ...inputs, annualReturn: v })}
              min={0}
              max={20}
              step={0.5}
              unit="%"
            />
          </div>
        </div>
      </div>

      {/* Results Section - 3 columns */}
      <div className="lg:col-span-3 space-y-6">
        {/* Main Result Card */}
        <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-600 rounded-3xl p-10 shadow-modern-xl text-white">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
            Projected Balance at Age {inputs.retirementAge}
          </div>
          <div className="text-6xl font-bold mb-2">
            ${results.futureValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-lg opacity-90">
            After {results.years} years of saving
          </div>
        </div>

        {/* Breakdown Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <ResultCard
            label="Total Contributions"
            value={`$${results.totalContributions.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Your money + employer match"
            gradient="from-emerald-500 to-teal-600"
          />
          <ResultCard
            label="Investment Growth"
            value={`$${results.earnings.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Earnings from compound interest"
            gradient="from-purple-500 to-pink-600"
          />
        </div>

        {/* Additional Insights */}
        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Key Insights</h3>
          <div className="space-y-4">
            <InsightRow
              label="Monthly employer contribution"
              value={`$${(inputs.monthlyContribution * (inputs.employerMatch / 100)).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            />
            <InsightRow
              label="Total monthly savings"
              value={`$${(inputs.monthlyContribution * (1 + inputs.employerMatch / 100)).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            />
            <InsightRow
              label="Return on investment"
              value={`${((results.earnings / results.totalContributions) * 100).toFixed(1)}%`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} ${unit ? 'pr-12' : 'pr-4'} py-3.5 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all font-semibold text-slate-900 text-lg`}
        />
        {unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultCard({
  label,
  value,
  description,
  gradient,
}: {
  label: string;
  value: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-modern-lg text-white`}>
      <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{label}</div>
      <div className="text-4xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{description}</div>
    </div>
  );
}

function InsightRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 font-medium">{label}</span>
      <span className="text-slate-900 font-bold text-lg">{value}</span>
    </div>
  );
}